import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
};

/**
 * Drag-and-drop upload zone. Shows a thumbnail after a file is selected.
 */
export default function UploadScreen({ imageFile, setImageFile }) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setImageFile(acceptedFiles[0]);
      }
    },
    [setImageFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxFiles: 1,
    multiple: false,
  });

  const previewUrl = imageFile ? URL.createObjectURL(imageFile) : null;

  return (
    <div className="mt-6">
      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={`relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed
                    cursor-pointer transition-all duration-200 py-12 px-6
                    ${isDragActive
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                    }`}
      >
        <input {...getInputProps()} />

        {previewUrl ? (
          /* Thumbnail preview */
          <div className="flex flex-col items-center gap-3">
            <img
              src={previewUrl}
              alt="Uploaded scan preview"
              className="max-h-48 max-w-xs rounded-xl object-contain shadow-md border border-gray-200"
            />
            <p className="text-sm text-gray-500 text-center">
              <span className="font-medium text-gray-700">{imageFile.name}</span>
              <br />
              <span className="text-xs text-gray-400">
                {(imageFile.size / 1024).toFixed(1)} KB
              </span>
            </p>
            <p className="text-xs text-blue-500 hover:underline cursor-pointer">
              Click or drag to replace
            </p>
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="text-5xl select-none">🩻</span>
            {isDragActive ? (
              <p className="text-blue-600 font-medium text-base">Drop your scan here…</p>
            ) : (
              <>
                <p className="text-gray-600 font-medium text-base">
                  Drag &amp; drop your scan here
                </p>
                <p className="text-sm text-gray-400">or click to browse files</p>
                <p className="text-xs text-gray-400 mt-1">
                  Supports JPG, PNG, WebP — X-rays, MRIs, skin photos
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
